import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiFilter,
  FiFolder,
  FiGitBranch,
  FiRefreshCcw,
  FiSearch,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";

/* =========================================================
   HELPERS
========================================================= */

const normalizeId = (
  value
) => {
  if (!value) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  if (
    typeof value ===
    "object"
  ) {
    return (
      value._id ||
      value.id ||
      ""
    );
  }

  return String(value);
};

/* =========================================================
   EXTRACT DROPDOWN CATEGORIES

   Backend response:

   {
     status: "success",
     data: [...]
   }
========================================================= */

const extractCategories = (
  response
) => {
  if (
    Array.isArray(
      response?.data
    )
  ) {
    return response.data;
  }

  if (
    Array.isArray(
      response?.data
        ?.categories
    )
  ) {
    return response.data
      .categories;
  }

  if (
    Array.isArray(
      response?.categories
    )
  ) {
    return response.categories;
  }

  return [];
};

/* =========================================================
   COMPONENT
========================================================= */

const CategoryFilters = ({
  filters,

  setFilters,

  loading,

  onSearch,

  onReset,
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );

  /* =======================================================
     STATE
  ======================================================= */

  const [
    parentCategories,
    setParentCategories,
  ] =
    useState([]);

  const [
    loadingParents,
    setLoadingParents,
  ] =
    useState(false);

  /* =======================================================
     LOAD CATEGORY DROPDOWN

     IMPORTANT:

     We now use:

     POST
     /inventory/categories/dropdown

     Instead of:

     POST
     /inventory/categories/list

     Backend returns every hierarchy level:

     Laptop
     Laptop > Business Laptop
     Laptop > Business Laptop > Dell
     Laptop > Business Laptop > Dell > Latitude
  ======================================================= */

  useEffect(
    () => {
      let mounted =
        true;

      const loadParentCategories =
        async () => {
          try {
            setLoadingParents(
              true
            );

            const response =
              await api.post(
                config
                  .getInventoryCategoryDropdown,

                {
                  search:
                    "",

                  /*
                   * Filters should also allow
                   * selecting inactive parent
                   * categories when reviewing
                   * existing data.
                   */

                  includeInactive:
                    true,

                  /*
                   * We are not editing a category
                   * from this filter component,
                   * therefore nothing needs to be
                   * excluded.
                   */

                  excludeCategoryId:
                    "",
                }
              );

            if (!mounted) {
              return;
            }

            const records =
              extractCategories(
                response
              );

            setParentCategories(
              records
            );
          } catch (
            error
          ) {
            console.error(
              "Load category filter options error:",
              error
            );

            if (mounted) {
              setParentCategories(
                []
              );
            }
          } finally {
            if (mounted) {
              setLoadingParents(
                false
              );
            }
          }
        };

      loadParentCategories();

      return () => {
        mounted =
          false;
      };
    },
    [
      api,
    ]
  );

  /* =======================================================
     CHANGE FIELD
  ======================================================= */

  const handleChange =
    (
      event
    ) => {
      const {
        name,
        value,
        type,
        checked,
      } =
        event.target;

      setFilters(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,
        })
      );
    };

  /* =======================================================
     SEARCH ON ENTER
  ======================================================= */

  const handleKeyDown =
    (
      event
    ) => {
      if (
        event.key ===
        "Enter"
      ) {
        event.preventDefault();

        if (!loading) {
          onSearch?.();
        }
      }
    };

  /* =======================================================
     RESET
  ======================================================= */

  const handleReset =
    () => {
      if (loading) {
        return;
      }

      onReset?.();
    };

  /* =======================================================
     HAS FILTERS
  ======================================================= */

  const hasFilters =
    Boolean(
      filters?.search ||
      filters?.status ||
      filters
        ?.parentCategory ||
      filters?.rootOnly ||
      filters?.level !==
        ""
    );

  /* =======================================================
     CATEGORY OPTIONS

     Backend already returns:

     path:
     Laptop > Business > Dell

     label:
     Laptop > Business > Dell

     level:
     2

     Therefore frontend does NOT need to rebuild
     the hierarchy anymore.
  ======================================================= */

  const categoryOptions =
    useMemo(
      () => {
        return [
          ...parentCategories,
        ].sort(
          (
            a,
            b
          ) => {
            /* =============================================
               Primary:
               full hierarchy path
            ============================================= */

            const pathA =
              String(
                a?.path ||
                  a?.label ||
                  a?.name ||
                  ""
              );

            const pathB =
              String(
                b?.path ||
                  b?.label ||
                  b?.name ||
                  ""
              );

            return pathA
              .localeCompare(
                pathB
              );
          }
        );
      },
      [
        parentCategories,
      ]
    );

  /* =======================================================
     GET OPTION LABEL

     Backend example:

     {
       name: "Dell",
       level: 2,
       path:
         "Laptop > Business Laptop > Dell"
     }

     Display:

     └─ └─ Dell

     Full path is also shown in title.
  ======================================================= */

  const getCategoryOptionLabel =
    (
      category
    ) => {
      const level =
        Math.max(
          Number(
            category?.level ||
              0
          ),
          0
        );

      const prefix =
        level > 0
          ? `${"— ".repeat(
              level
            )}`
          : "";

      return `${prefix}${
        category?.name ||
        ""
      }`;
    };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-category-filters"
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <div
        className="inventory-category-filters-header"
      >
        <div>
          <FiFilter />

          <div>
            <strong>
              Filter Categories
            </strong>

            <span>
              Search and filter your
              category hierarchy
            </span>
          </div>
        </div>

        {hasFilters ? (
          <button
            type="button"
            className="inventory-category-filter-clear"
            onClick={
              handleReset
            }
            disabled={
              loading
            }
          >
            <FiX />

            Clear Filters
          </button>
        ) : null}
      </div>

      {/* ===================================================
          FILTER BODY
      =================================================== */}

      <div
        className="inventory-category-filters-grid"
      >
        {/* =================================================
            SEARCH
        ================================================= */}

        <div
          className="
            inventory-category-filter-group
            inventory-category-filter-search
          "
        >
          <label>
            Search
          </label>

          <div
            className="inventory-category-filter-input-icon"
          >
            <FiSearch />

            <input
              type="text"
              name="search"
              value={
                filters
                  ?.search ||
                ""
              }
              onChange={
                handleChange
              }
              onKeyDown={
                handleKeyDown
              }
              placeholder="Search category name..."
            />
          </div>
        </div>

        {/* =================================================
            STATUS
        ================================================= */}

        <div
          className="inventory-category-filter-group"
        >
          <label>
            Status
          </label>

          <select
            name="status"
            value={
              filters
                ?.status ||
              ""
            }
            onChange={
              handleChange
            }
          >
            <option
              value=""
            >
              All Status
            </option>

            <option
              value="active"
            >
              Active
            </option>

            <option
              value="inactive"
            >
              Inactive
            </option>
          </select>
        </div>

        {/* =================================================
            PARENT CATEGORY

            Every hierarchy level is available.

            Example:

            Laptop
            — Business Laptop
            — — Dell
            — — — Latitude
        ================================================= */}

        <div
          className="inventory-category-filter-group"
        >
          <label>
            Parent Category
          </label>

          <div
            className="inventory-category-filter-select-icon"
          >
            <FiFolder />

            <select
              name="parentCategory"
              value={
                normalizeId(
                  filters
                    ?.parentCategory
                )
              }
              onChange={
                handleChange
              }
              disabled={
                loadingParents
              }
            >
              <option
                value=""
              >
                {loadingParents
                  ? "Loading categories..."
                  : "All Parent Categories"}
              </option>

              {categoryOptions.map(
                (
                  category
                ) => (
                  <option
                    key={
                      category._id
                    }
                    value={
                      category._id
                    }
                    title={
                      category
                        ?.path ||
                      category
                        ?.label ||
                      category
                        ?.name
                    }
                  >
                    {
                      getCategoryOptionLabel(
                        category
                      )
                    }
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {/* =================================================
            LEVEL
        ================================================= */}

        <div
          className="inventory-category-filter-group"
        >
          <label>
            Hierarchy Level
          </label>

          <div
            className="inventory-category-filter-select-icon"
          >
            <FiGitBranch />

            <select
              name="level"
              value={
                filters?.level ??
                ""
              }
              onChange={
                handleChange
              }
            >
              <option
                value=""
              >
                All Levels
              </option>

              <option
                value="0"
              >
                Level 0 — Root
              </option>

              <option
                value="1"
              >
                Level 1
              </option>

              <option
                value="2"
              >
                Level 2
              </option>

              <option
                value="3"
              >
                Level 3
              </option>

              <option
                value="4"
              >
                Level 4+
              </option>
            </select>
          </div>
        </div>

        {/* =================================================
            ROOT ONLY
        ================================================= */}

        <div
          className="
            inventory-category-filter-group
            inventory-category-filter-checkbox-group
          "
        >
          <label>
            Category Type
          </label>

          <label
            className="inventory-category-root-toggle"
          >
            <input
              type="checkbox"
              name="rootOnly"
              checked={
                Boolean(
                  filters
                    ?.rootOnly
                )
              }
              onChange={
                handleChange
              }
            />

            <span
              className="inventory-category-root-toggle-control"
            />

            <span
              className="inventory-category-root-toggle-label"
            >
              Root categories only
            </span>
          </label>
        </div>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <div
          className="inventory-category-filter-actions"
        >
          <button
            type="button"
            className="inventory-category-filter-search-button"
            onClick={
              onSearch
            }
            disabled={
              loading
            }
          >
            {loading ? (
              <FiRefreshCcw
                className="spinning"
              />
            ) : (
              <FiSearch />
            )}

            {loading
              ? "Searching..."
              : "Search"}
          </button>

          <button
            type="button"
            className="inventory-category-filter-reset-button"
            onClick={
              handleReset
            }
            disabled={
              loading
            }
          >
            <FiRefreshCcw />

            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryFilters;